import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { PerInfoBoxNumberElement } from '../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { EmployeeLevelEnum, SkillEmployeeStatusEnum, Country } from '../../../types'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import './index.less'
import {
  areAppraisalTabEmployeeFiltersEquals,
  AppraisalTabEmployeeFilter,
  ExtendedEmployeesAndAppraisalsFragment,
  possibleAppraisalSeasons,
  possibleOffices as getPossibleOffices,
  appraisalTabInitialFilter,
  APPRAISAL_NO_OFFICE_FILTER_VALUE,
} from './appraisal-util'
import useGetCountriesWithBranchOfficeVisibleFlag from '../../../graphql/countrySettings/hooks/useGetCountriesWithBranchOfficeVisibleFlag'
import { Col, Select, Divider, Icon } from 'antd'
import PerText from '../../../components/custom_components/Text'
import PerButton from '../../../components/custom_components/Button'
import { getSortOrderByEmployeeLevel } from '../employees/employeesLevelSorterUtil'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import {
  refreshEmployeesOfCompanySkillStatuses,
  refreshEmployeesOfBUSkillStatuses,
} from '../../../graphql/skill-status/useSkillStatusMutations'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import {
  SkillStatusCompleted,
  SkillStatusOutdated,
  SkillStatusMissing,
  SkillStatusCvIncompleteOrNearlyOutdated,
} from '../../skillStatus/SkillStatusLabelComponent'
import { TFunction } from 'i18next'
import { showNotification } from '../../../components/custom_components/Notification/notification'
const { Option } = Select

const AppraisalFilterField: FC<{ label: string; children: ReactNode; className?: string }> = ({ label, children, className }) => (
  <PerFlexRow direction="column" width="fullWidth" className={className}>
    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
      {label}
    </PerText>
    {children}
  </PerFlexRow>
)

interface Props {
  filteredEmployees: number
  currentAppraisalSeasonYear?: number
  companyOrBuId: string
  country?: Country
  employeeLevels: EmployeeLevelEnum[]
  tableData: ExtendedEmployeesAndAppraisalsFragment[]
  employeeFilter: AppraisalTabEmployeeFilter
  setEmployeeFilter: (value: AppraisalTabEmployeeFilter) => void
  oldestSyncUTCDate?: string
}

export const CompanySettingsAppraisalInfoBox: FC<Props> = ({
  filteredEmployees,
  tableData,
  employeeLevels,
  employeeFilter,
  setEmployeeFilter,
  currentAppraisalSeasonYear,
  companyOrBuId,
  country,
  oldestSyncUTCDate,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateTimeShort } = useDateTime(user)
  const possibleSeasons = possibleAppraisalSeasons(tableData)
  const possibleLevels: EmployeeLevelEnum[] = [...new Set(tableData.map((e) => e.employee.currentLevel as EmployeeLevelEnum))]
  const possibleBUs: string[] = [...new Set(tableData.map((e) => e.employee.buName))]
  const possibleOffices = getPossibleOffices(tableData)
  const { data: branchOfficeVisibleFlags } = useGetCountriesWithBranchOfficeVisibleFlag()
  const countriesWhereOfficeIsVisible = new Set(
    (branchOfficeVisibleFlags?.getCountriesWithBranchOfficeVisibleFlag ?? [])
      .filter((item) => item.isBranchOfficeVisible)
      .map((item) => item.country),
  )
  const showOfficeFilter =
    countriesWhereOfficeIsVisible.has(user.country as Country) && (!country || countriesWhereOfficeIsVisible.has(country))
  const [isRefreshSkillStatusProcessing, toggleRefreshSkillStatusProcessing] = useState<boolean>(false)
  const [skillStatusError, setSkillStatusError] = useState<boolean>(false)
  const atLeastOneExpiredOrUnknownSkillStatus: boolean = tableData.some((sub) => !sub.skillStatus || sub.skillStatus.expired)

  const yes = t('global.yes')
  const no = t('global.no')

  const buildSemaphoreText = (t: TFunction, color: string) => {
    return t(`profile.skillCenter.public.label.filters.${color}`)
  }

  return (
    <PerInfoBox>
      <PerFlexRow align="middle" justify="space-between" width="fullWidth" className="fullHeight">
        <Col span={15} className="fullHeight">
          <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
            <PerText mb="xxs" appearance="tile-heading">
              {t('companySettingsView.appraisalTab.filter.heading')}
            </PerText>

            <div className="appraisal-tab-filters">
              <AppraisalFilterField label={t('companySettingsView.appraisalTab.filter.level')}>
                <Select
                  allowClear
                  onChange={(value: EmployeeLevelEnum) => {
                    setEmployeeFilter({ ...employeeFilter, employeeLevel: value })
                  }}
                  value={employeeFilter.employeeLevel}
                  placeholder={t('companySettingsView.appraisalTab.filter.levelPlaceholder')}
                  style={{ width: '100%' }}
                >
                  {possibleLevels
                    .sort((levelA: EmployeeLevelEnum, levelB: EmployeeLevelEnum) => {
                      return getSortOrderByEmployeeLevel(levelA, employeeLevels) - getSortOrderByEmployeeLevel(levelB, employeeLevels)
                    })
                    .map((level) => (
                      <Option key={level} value={level}>
                        {level}
                      </Option>
                    ))}
                </Select>
              </AppraisalFilterField>
              <AppraisalFilterField label={t('companySettingsView.appraisalTab.filter.bu')}>
                <Select
                  allowClear
                  onChange={(value: string) => {
                    setEmployeeFilter({ ...employeeFilter, bu: value })
                  }}
                  value={employeeFilter.bu}
                  placeholder={t('companySettingsView.appraisalTab.filter.buPlaceholder')}
                  style={{ width: '100%' }}
                >
                  {possibleBUs
                    .sort((buA: string, buB: string) => {
                      return buA.localeCompare(buB)
                    })
                    .map((bu) => (
                      <Option key={bu} value={bu}>
                        {bu}
                      </Option>
                    ))}
                </Select>
              </AppraisalFilterField>
              <AppraisalFilterField label={t('companySettingsView.appraisalTab.infoBox.mandatory.heading')}>
                <Select
                  allowClear
                  onChange={(value: string) => {
                    setEmployeeFilter({ ...employeeFilter, mandatory: value === yes ? true : value === no ? false : undefined })
                  }}
                  value={employeeFilter.mandatory !== undefined ? (employeeFilter.mandatory ? yes : no) : undefined}
                  placeholder={t('companySettingsView.appraisalTab.filter.mandatoryPlaceholder')}
                  style={{ width: '100%' }}
                >
                  <Option key={yes} value={yes}>
                    {yes}
                  </Option>
                  <Option key={no} value={no}>
                    {no}
                  </Option>
                </Select>
              </AppraisalFilterField>
              <AppraisalFilterField
                label={
                  !!currentAppraisalSeasonYear
                    ? t('companySettingsView.appraisalTab.infoBox.todo.heading', { asYear: currentAppraisalSeasonYear })
                    : t('companySettingsView.appraisalTab.infoBox.todo.buHeading')
                }
              >
                <Select
                  allowClear
                  onChange={(value: string) => {
                    setEmployeeFilter({ ...employeeFilter, todo: value === yes ? true : value === no ? false : undefined })
                  }}
                  value={employeeFilter.todo !== undefined ? (employeeFilter.todo ? yes : no) : undefined}
                  placeholder={t('companySettingsView.appraisalTab.filter.todoPlaceholder')}
                  style={{ width: '100%' }}
                >
                  <Option key={yes} value={yes}>
                    {t('global.yes')}
                  </Option>
                  <Option key={no} value={no}>
                    {no}
                  </Option>
                </Select>
              </AppraisalFilterField>
              <div>
                <PerButtonCircle
                  icon="info_fill"
                  color="secondary"
                  variant="pastel"
                  className="disable-shadow"
                  tooltip={t('companySettingsView.appraisalTab.infoBox.todo.subtitle')}
                />
              </div>

              {showOfficeFilter && (
                <AppraisalFilterField label={t('companySettingsView.appraisalTab.filter.office')}>
                  <Select
                    allowClear
                    onChange={(value: string) => {
                      setEmployeeFilter({ ...employeeFilter, office: value })
                    }}
                    value={employeeFilter.office}
                    placeholder={t('companySettingsView.appraisalTab.filter.officePlaceholder')}
                    style={{ width: '100%' }}
                  >
                    {possibleOffices.map((office) => (
                      <Option key={office} value={office}>
                        {office === APPRAISAL_NO_OFFICE_FILTER_VALUE
                          ? t('companySettingsView.appraisalTab.table.office.notAvailable')
                          : office}
                      </Option>
                    ))}
                  </Select>
                </AppraisalFilterField>
              )}
              <AppraisalFilterField label={t('employeeList.infoBox.filters.skillStatus')}>
                <Select
                  allowClear
                  onChange={(value: SkillEmployeeStatusEnum) => {
                    setEmployeeFilter({ ...employeeFilter, skillStatus: value })
                  }}
                  value={employeeFilter.skillStatus}
                  placeholder={t('companySettingsView.appraisalTab.filter.skillStatusPlaceholder')}
                  style={{ width: '100%' }}
                >
                  <Option
                    key={SkillEmployeeStatusEnum.Completed}
                    value={SkillEmployeeStatusEnum.Completed}
                    title={buildSemaphoreText(t, 'green')}
                  >
                    <PerFlexRow align="middle" noWrap className="overflow--hidden">
                      <SkillStatusCompleted size={20} />
                      <div className="appraisal-tab-filters__skill-option-label">
                        <PerText ml="xxxs" fontSize="s" className="truncate-text">
                          {buildSemaphoreText(t, 'green')}
                        </PerText>
                      </div>
                    </PerFlexRow>
                  </Option>
                  <Option
                    key={SkillEmployeeStatusEnum.CvIncomplete}
                    value={SkillEmployeeStatusEnum.CvIncomplete}
                    title={buildSemaphoreText(t, 'yellow')}
                  >
                    <PerFlexRow align="middle" noWrap className="overflow--hidden">
                      <SkillStatusCvIncompleteOrNearlyOutdated size={20} />
                      <div className="appraisal-tab-filters__skill-option-label">
                        <PerText ml="xxxs" fontSize="s" className="truncate-text">
                          {buildSemaphoreText(t, 'yellow')}
                        </PerText>
                      </div>
                    </PerFlexRow>
                  </Option>
                  <Option
                    key={SkillEmployeeStatusEnum.Outdated}
                    value={SkillEmployeeStatusEnum.Outdated}
                    title={buildSemaphoreText(t, 'red')}
                  >
                    <PerFlexRow align="middle" noWrap className="overflow--hidden">
                      <SkillStatusOutdated size={20} />
                      <div className="appraisal-tab-filters__skill-option-label">
                        <PerText ml="xxxs" fontSize="s" className="truncate-text">
                          {buildSemaphoreText(t, 'red')}
                        </PerText>
                      </div>
                    </PerFlexRow>
                  </Option>
                  <Option
                    key={SkillEmployeeStatusEnum.Missing}
                    value={SkillEmployeeStatusEnum.Missing}
                    title={buildSemaphoreText(t, 'missing')}
                  >
                    <PerFlexRow align="middle" noWrap className="overflow--hidden">
                      <SkillStatusMissing size={20} />
                      <div className="appraisal-tab-filters__skill-option-label">
                        <PerText ml="xxxs" fontSize="s" className="truncate-text">
                          {buildSemaphoreText(t, 'missing')}
                        </PerText>
                      </div>
                    </PerFlexRow>
                  </Option>
                </Select>
              </AppraisalFilterField>
              <AppraisalFilterField
                className="appraisal-tab-filters__span2"
                label={t('companySettingsView.appraisalTab.filter.appraisalSeasons')}
              >
                <Select
                  mode="multiple"
                  onChange={(values: string[]) => {
                    setEmployeeFilter({ ...employeeFilter, appraisalSeasonYears: values.sort((a, b) => a.localeCompare(b)) })
                  }}
                  value={employeeFilter.appraisalSeasonYears}
                  placeholder={t('companySettingsView.appraisalTab.filter.seasonsPlaceholder')}
                  style={{ width: '100%' }}
                  maxTagCount={2}
                >
                  {possibleSeasons
                    .sort((yearA: string, yearB: string) => yearA.localeCompare(yearB))
                    .map((year) => (
                      <Option key={year} value={year}>
                        {year}
                      </Option>
                    ))}
                </Select>
              </AppraisalFilterField>
              {!showOfficeFilter && <div />}
              <div aria-hidden>
                <PerButtonCircle
                  icon="info_fill"
                  color="secondary"
                  variant="pastel"
                  className="disable-shadow"
                  style={{ visibility: 'hidden' }}
                  tabIndex={-1}
                />
              </div>
            </div>
          </PerFlexRow>
        </Col>

        <Divider type="vertical" className="fullHeight" />

        <InfoBoxTile
          heading={t('companySettingsView.appraisalTab.filter.counters')}
          contentFlex="fixed"
          headingAlign="center"
          contentAlign="end"
          childrenFlexDirection="column"
        >
          <PerFlexRow direction="column" noWrap>
            <PerInfoBoxNumberElement numberFontSize="xxl" outOfFontSize="l" number={filteredEmployees} outOf={`${tableData.length}`} />
            <PerButton
              mt="xxs"
              text={t('companySettingsView.appraisalTab.filter.reset')}
              width="fullWidth"
              color="error"
              type="button"
              variant="pastel"
              before="delete_fill"
              onClick={() => {
                setEmployeeFilter(appraisalTabInitialFilter)
              }}
              disabled={areAppraisalTabEmployeeFiltersEquals(appraisalTabInitialFilter, employeeFilter)}
            />
          </PerFlexRow>
        </InfoBoxTile>

        <Divider type="vertical" className="fullHeight" />

        <Col span={4} className="fullHeight">
          <InfoBoxTile
            heading={t('employeeList.infoBox.skillStatus')}
            contentFlex="fixed"
            headingAlign="center"
            contentAlign="space-between"
          >
            <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
              <PerFlexRow justify="center">
                <PerText fontSize="xs" color="secondary" appearance="quote" align="center">
                  {oldestSyncUTCDate
                    ? t('employeeList.infoBox.skillStatusDescription', { date: formatDateTimeShort(oldestSyncUTCDate) })
                    : t('employeeList.infoBox.skillStatusDescription2')}
                </PerText>
              </PerFlexRow>
              <PerButtonCircle
                tooltip={
                  skillStatusError
                    ? t('employeeList.infoBox.refreshError')
                    : !atLeastOneExpiredOrUnknownSkillStatus
                    ? t('employeeList.infoBox.noRefreshRequired')
                    : t('profile.skillCenter.public.refreshStatusButton')
                }
                color={skillStatusError ? 'error' : 'primary'}
                variant="pastel"
                icon={skillStatusError ? 'warning_fill' : !atLeastOneExpiredOrUnknownSkillStatus ? 'check_stroke' : undefined}
                onClick={async () => {
                  toggleRefreshSkillStatusProcessing(true)
                  const { data: skillStatusData, errors: skillStatusErrors } = country
                    ? await refreshEmployeesOfCompanySkillStatuses(companyOrBuId, country)
                    : await refreshEmployeesOfBUSkillStatuses(companyOrBuId)
                  if (!skillStatusData && skillStatusErrors) {
                    showNotification({
                      type: 'error',
                      message: t('employeeList.infoBox.notifications.skillStatusSync.error.title'),
                      description: t('employeeList.infoBox.notifications.skillStatusSync.error.description'),
                    })
                    setSkillStatusError(true)
                  } else {
                    showNotification({
                      type: 'success',
                      message: t('employeeList.infoBox.notifications.skillStatusSync.success.title'),
                      description: t('employeeList.infoBox.notifications.skillStatusSync.success.description'),
                    })
                  }
                  toggleRefreshSkillStatusProcessing(false)
                }}
                size="large"
                isProcessing={{ active: isRefreshSkillStatusProcessing }}
                disabled={!atLeastOneExpiredOrUnknownSkillStatus || skillStatusError}
                customIconComponent={
                  skillStatusError || !atLeastOneExpiredOrUnknownSkillStatus ? undefined : <Icon type="reload" style={{ fontSize: 16 }} />
                }
              />
            </PerFlexRow>
          </InfoBoxTile>
        </Col>
      </PerFlexRow>
    </PerInfoBox>
  )
}
